import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordInput } from '@xxtract/ui';
import { useAuth } from '../hooks/useAuth';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';

export interface ChangePasswordFormProps {
  /**
   * Called after a successful change, IN ADDITION to the built-in success message. Only the
   * forced first-login flow (ChangePasswordPage) needs to navigate away afterwards — leave
   * this unset (e.g. from the "Mijn gegevens" ChangePasswordCard, h12-3) to stay on the page.
   */
  onSuccess?: () => void;
}

export function ChangePasswordForm({ onSuccess }: ChangePasswordFormProps = {}) {
  const { t } = useTranslation();
  const { changePasswordMutation } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;
    changePasswordMutation.mutate({ currentPassword, newPassword }, { onSuccess });
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="change-password-form"
      className="max-w-sm"
    >
      {changePasswordMutation.isError && (
        <ApiErrorMessage error={changePasswordMutation.error} />
      )}

      {changePasswordMutation.isSuccess && (
        <div
          className="mb-4 flex items-start gap-2.5 rounded-[10px] p-3 px-4"
          style={{
            backgroundColor: 'var(--success-light)',
            border: '1px solid rgba(183, 217, 69, 0.35)',
          }}
        >
          <span
            className="material-symbols-outlined shrink-0"
            style={{ fontSize: '16px', color: 'var(--success-dark)' }}
          >
            check_circle
          </span>
          <p className="text-xs font-semibold" style={{ color: 'var(--success-dark)' }}>
            {t('auth.passwordChanged')}
          </p>
        </div>
      )}

      <div className="mb-3.5 flex flex-col gap-[5px]">
        <label
          htmlFor="currentPassword"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('auth.currentPassword')}
        </label>
        <PasswordInput
          id="currentPassword"
          data-testid="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          autoComplete="current-password"
          showLabel={t('auth.showPassword')}
          hideLabel={t('auth.hidePassword')}
          className="rounded-lg px-3 py-2 text-[0.8125rem]"
          style={{
            border: '1px solid var(--border)',
            backgroundColor: 'var(--background)',
            color: 'var(--foreground)',
          }}
        />
      </div>

      <div className="mb-3.5 flex flex-col gap-[5px]">
        <label
          htmlFor="newPassword"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('auth.newPassword')}
        </label>
        <PasswordInput
          id="newPassword"
          data-testid="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
          showLabel={t('auth.showPassword')}
          hideLabel={t('auth.hidePassword')}
          className="rounded-lg px-3 py-2 text-[0.8125rem]"
          style={{
            border: '1px solid var(--border)',
            backgroundColor: 'var(--background)',
            color: 'var(--foreground)',
          }}
        />
        <p className="text-[0.6875rem]" style={{ color: 'var(--foreground-muted)' }}>
          {t('auth.passwordRequirements')}
        </p>
      </div>

      <div className="mb-6 flex flex-col gap-[5px]">
        <label
          htmlFor="confirmPassword"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('auth.confirmPassword')}
        </label>
        <PasswordInput
          id="confirmPassword"
          data-testid="confirm-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          autoComplete="new-password"
          showLabel={t('auth.showPassword')}
          hideLabel={t('auth.hidePassword')}
          className="rounded-lg px-3 py-2 text-[0.8125rem]"
          style={{
            border: `1px solid ${confirmPassword && newPassword !== confirmPassword ? 'var(--destructive)' : 'var(--border)'}`,
            backgroundColor: 'var(--background)',
            color: 'var(--foreground)',
          }}
        />
        {confirmPassword && newPassword !== confirmPassword && (
          <p className="text-[0.6875rem]" style={{ color: 'var(--destructive)' }}>
            {t('auth.passwordsDoNotMatch')}
          </p>
        )}
      </div>

      <button
        type="submit"
        data-testid="change-password-submit"
        disabled={changePasswordMutation.isPending || newPassword !== confirmPassword}
        className="rounded-lg px-4 py-2 text-[0.8125rem] font-medium text-white transition-all duration-100 disabled:opacity-60"
        style={{
          backgroundColor: 'var(--primary)',
          border: '1px solid var(--primary)',
        }}
      >
        {changePasswordMutation.isPending
          ? t('common.loading')
          : t('auth.changePassword')}
      </button>
    </form>
  );
}
